import React, { useContext, useEffect, useRef, useState } from 'react';
import { NextImage } from '@sdf/base';
import { Styles } from '@t2c/shared-fe';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import {
  CircularProgress,
  IconButton,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { CreditDialog } from '../../create-video/CreditDialog';
import { useTextToImagePage } from '../../../../t2c-fe/hooks/textToImage/useTextToImagePage';
import { TextToImageProccessingModal } from '../../text-to-image/TextToImageProccessingModal';
import {
  AiImageModelType,
  imageModelDetails,
  NumGenerationsEnum,
  OrientationEnum,
} from '../../../utils/constant';
import { AspectRatios } from '@t2c/ai-video-editor';
import { EXTENDED_ASPECT_RATIO_OPTIONS } from 'apps/t2c-fe/utils';
import { UploadImage } from '../../broll/UploadImage';
import { useDialog } from 'apps/t2c-fe/hooks';
import { BrowseBRollModal } from '../../edit-video/modals/BrowseBRoll/BrowseBRollModal';
import { useRouter } from 'next/router';
import { CreditContext } from 'apps/t2c-fe/context/CreditProvider';
import { PremiumBadge } from '../../broll/BrollPage';
import { RenderGateModal } from '../../common/RenderGateModal';

interface Props {
  refetch: () => {};
}
export const AiImageWorkBench: React.FC<Props> = ({ refetch }) => {
  const {
    imageGenerationData,
    setImageGenerationData,
    maxCharLimit,
    setMaxCharLimit,
    handleDataChange,
    handleimageGenerate,
    isCreditDialogOpen,
    closeCreditDialog,
    isFetchingRequiredCredits,
    requiredCreditsData,
    handleConfirmGeneration,
    imageGenerationLoading,
    isProccessingModalOpen,
    closeProccessingModal,
    handleUpgrade,
    uploadURL,
    setUploadURL,
    secondUploadURL,
    setSecondUploadURL,
    setIsSelectedSecond,
    handleImageSelect,
    handleUpdateImage,
    handleUpdateSecondImage,
    isEnhancing,
    isUploading,
    applyModelDefaults,
    abortController,
    abortControllerSecond,
    setIsUploading,
    isUploadingSecond,
    setIsUploadingSecond,
    renderGateModalMode,
    openRenderGateModal,
    setRenderGateModalMode,
    setOpenRenderGateModal,
    handlePromptEnhance,
    newGenerationData,
    preImageValidator,
  } = useTextToImagePage({ refetchimages: refetch });

  const theme = useTheme();
  const isExtraSmall = useMediaQuery(theme.breakpoints.down('sm'));
  const isSmall = useMediaQuery(theme.breakpoints.down('md'));
  const { query, push } = useRouter();
  const { modelDetails, getModelData, userPlanData, agentPlanData } = useContext(CreditContext);

  const freeTrialAccess = userPlanData === 'freeTrialAccess' || agentPlanData === 'agentTrialPlan';

  const {
    isDialogOpen: isOpenBrowseBRollModal,
    openDialog: openBrowseBRollModal,
    closeDialog: CloseBrowseBRollModal,
  } = useDialog();

  const handleClickImage = () => {
    setIsSelectedSecond(false);
    openBrowseBRollModal();
  };

  const handleClickSecondImage = () => {
    setIsSelectedSecond(true);
    openBrowseBRollModal();
  };

  return (
    <Grid container gap={3}>
      <Grid item xs={12}>
        <Typography sx={styles.heading}>Describe the image you want to create. </Typography>
        <Box sx={{ position: 'relative' }}>
          <TextField
            placeholder="E.g., A serene mountain landscape at sunrise."
            size="medium"
            type="text"
            name="prompt"
            multiline
            rows={isExtraSmall ? 2 : 9}
            sx={styles.textField}
            inputProps={{ maxLength: maxCharLimit }}
            onChange={(e) => handleDataChange('prompt', e.target.value?.slice(0, maxCharLimit))}
            value={imageGenerationData.prompt}
            fullWidth
          />
          <Tooltip title="Enhance prompt with AI" placement="top">
            <IconButton
              size="small"
              sx={{
                position: 'absolute',
                bottom: 0,
                right: 4,
                zIndex: 10,
              }}
              disabled={!imageGenerationData.prompt?.trim() || isEnhancing}
              onClick={handlePromptEnhance}
            >
              {isEnhancing ? (
                <CircularProgress size={20} />
              ) : (
                <NextImage
                  src={'/assets/icons/create-video_default.svg'}
                  alt=""
                  width={24}
                  height={24}
                />
              )}
            </IconButton>
          </Tooltip>
        </Box>
        <Box>
          <Typography
            variant="body2"
            sx={{ color: (theme) => theme.palette.text.placeholder, pl: '2px', pt: '2px' }}
          >
            {imageGenerationData?.prompt?.length || 0}/{maxCharLimit} characters
          </Typography>
        </Box>
      </Grid>
      <Box display={'flex'} gap={2} flexDirection={'row'} flex={1}>
        <Box flex={1}>
          <Typography sx={styles.label}>Enter Name</Typography>
          <TextField
            placeholder="Type name here"
            size="medium"
            type="text"
            name="name"
            fullWidth
            sx={styles.textField}
            onChange={(e) => handleDataChange('name', e.target.value)}
            value={imageGenerationData.name}
          />
        </Box>
        <Box flex={1}>
          <Typography sx={styles.label}>Model</Typography>
          <Select
            labelId="model-select-label"
            value={imageGenerationData.model}
            onChange={(e) => applyModelDefaults(e.target.value as AiImageModelType)}
            sx={{
              width: '100%',
              backgroundColor: (theme) => theme.palette.background.secondary,
            }}
            MenuProps={{
              PaperProps: {
                sx: styles.dropdownProps,
              },
            }}
            renderValue={(selected) => {
              return (
                <Box>
                  {selected} {getModelData(selected)?.isPremium && <PremiumBadge />}
                </Box>
              );
            }}
          >
            {Object.values(AiImageModelType).map((output) => (
              <MenuItem key={output} value={output}>
                <Box>
                  {output === AiImageModelType.RUNWAYGEN4_TURBO ? (
                    <>
                      <Box>
                        {output} {getModelData(output)?.isPremium && <PremiumBadge />}
                      </Box>
                      <NextImage
                        src="/assets/RunwayLogo.svg"
                        alt="Runway"
                        width={100}
                        height={10}
                      />
                    </>
                  ) : !getModelData(output)?.isPremium ? (
                    freeTrialAccess ? (
                      <>
                        <Box>
                          {output}{' '}
                          <Typography component="span" sx={{ fontSize: '12px' }}>
                            Limited Access
                          </Typography>
                        </Box>
                        <Typography
                          sx={{
                            fontSize: '10px',
                            color: (theme) => theme.palette.text.primary,
                            opacity: 0.6,
                          }}
                        >
                          Upgrade for Unlimited Access
                        </Typography>
                      </>
                    ) : (
                      <Box>
                        {output} {getModelData(output)?.isPremium && <PremiumBadge />}
                      </Box>
                    )
                  ) : (
                    <Box>
                      {output} {getModelData(output)?.isPremium && <PremiumBadge />}
                    </Box>
                  )}
                </Box>
              </MenuItem>
            ))}
          </Select>
        </Box>
      </Box>
      <Box display={'flex'} gap={2} width={'100%'} flexDirection={isSmall ? 'column' : 'row'}>
        <Box display={'flex'} gap={2} flexDirection={'row'} flex={1}>
          <Box flex={1}>
            <Typography sx={styles.label}>Orientation</Typography>
            <Select
              labelId="orientation-select-label"
              value={imageGenerationData.orientation}
              onChange={(e) => handleDataChange('orientation', e.target.value as OrientationEnum)}
              sx={{
                width: '100%',
                backgroundColor: (theme) => theme.palette.background.secondary,
              }}
              MenuProps={{
                PaperProps: {
                  sx: styles.dropdownProps,
                },
              }}
            >
              {EXTENDED_ASPECT_RATIO_OPTIONS.filter((option) =>
                imageModelDetails[imageGenerationData.model].orientationPerModel.includes(
                  option.value
                )
              ).map(({ id, value, icon, label, name }) => (
                <MenuItem key={id} value={value} sx={styles.menuItem}>
                  <Stack direction="row" alignItems="center" gap={1}>
                    <Box sx={{ width: 35, display: 'flex', justifyContent: 'center' }}>{icon}</Box>
                    <Stack direction="row" alignItems="center" gap={1}>
                      <Typography variant="body2" sx={styles.menuLabel}>
                        {label} {name}
                      </Typography>
                    </Stack>
                  </Stack>
                </MenuItem>
              ))}
            </Select>
          </Box>
          <Box flex={1}>
            <Typography sx={styles.label}>Generations</Typography>
            <Select
              labelId="numGenerations-select-label"
              value={imageGenerationData.numGenerations}
              onChange={(e) =>
                handleDataChange('numGenerations', e.target.value as NumGenerationsEnum)
              }
              sx={{
                width: '100%',
                backgroundColor: (theme) => theme.palette.background.secondary,
              }}
              MenuProps={{
                PaperProps: {
                  sx: styles.dropdownProps,
                },
              }}
            >
              {Object.values(NumGenerationsEnum).map((output) => (
                <MenuItem key={output} value={output}>
                  {output}{' '}
                  {freeTrialAccess &&
                    !(
                      getModelData(imageGenerationData.model).alphaGenerationLimit ===
                      Number(output)
                    ) && <PremiumBadge />}
                </MenuItem>
              ))}
            </Select>
          </Box>
        </Box>
        <Box display={'flex'} gap={2} flexDirection={'row'} flex={1}>
          {imageModelDetails[imageGenerationData.model].resolutionPerModel.length > 0 && (
            <Box flex={1}>
              <Typography sx={styles.label}>Resolution</Typography>
              <Select
                labelId="resolution-select-label"
                value={imageGenerationData.resolution}
                onChange={(e) => handleDataChange('resolution', e.target.value)}
                sx={{
                  width: '100%',
                  backgroundColor: (theme) => theme.palette.background.secondary,
                }}
                MenuProps={{
                  PaperProps: {
                    sx: styles.dropdownProps,
                  },
                }}
              >
                {imageModelDetails[imageGenerationData.model].resolutionPerModel.map((output) => (
                  <MenuItem key={output} value={output}>
                    {output}
                  </MenuItem>
                ))}
              </Select>
            </Box>
          )}
          {imageModelDetails[imageGenerationData.model].stylesPerModel.length > 0 && (
            <Box flex={1}>
              <Typography sx={styles.label}>Style</Typography>
              <Select
                labelId="style-select-label"
                value={imageGenerationData.style.value}
                onChange={(e) => {
                  const selected = imageModelDetails[imageGenerationData.model].stylesPerModel.find(
                    (style) => style.value === e.target.value
                  );
                  setImageGenerationData((prev) => ({
                    ...prev,
                    style: selected,
                  }));
                }}
                sx={{
                  width: '100%',
                  backgroundColor: (theme) => theme.palette.background.secondary,
                }}
                MenuProps={{
                  PaperProps: {
                    sx: styles.dropdownProps,
                  },
                }}
              >
                {imageModelDetails[imageGenerationData.model].stylesPerModel.map((style) => (
                  <MenuItem key={style.value} value={style.value}>
                    {style.label}
                  </MenuItem>
                ))}
              </Select>
            </Box>
          )}
        </Box>
      </Box>
      <Grid item xs={12}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Grid container spacing={2}>
              {imageModelDetails[imageGenerationData.model].showUpload && (
                <Grid item xs={6}>
                  <Typography sx={styles.text}>
                    Reference Image{' '}
                    {imageModelDetails[imageGenerationData.model].isStartImageRequired ? '*' : ''}
                  </Typography>
                  <UploadImage
                    uploadURL={uploadURL}
                    openBrowseBRollModal={handleClickImage}
                    handleUpdateImage={handleUpdateImage}
                    abortController={abortController}
                    uploading={isUploading}
                    setUploading={setIsUploading}
                    limit={imageModelDetails[imageGenerationData.model].maxImageSize}
                    preImageValidator={preImageValidator}
                  />
                </Grid>
              )}
              {imageModelDetails[imageGenerationData.model].showSecondUpload && (
                <Grid item xs={6}>
                  <Typography sx={styles.text}>Reference Image 2</Typography>
                  <UploadImage
                    uploadURL={secondUploadURL}
                    openBrowseBRollModal={handleClickSecondImage}
                    handleUpdateImage={handleUpdateSecondImage}
                    abortController={abortControllerSecond}
                    uploading={isUploadingSecond}
                    setUploading={setIsUploadingSecond}
                    limit={imageModelDetails[imageGenerationData.model].maxImageSize}
                    preImageValidator={preImageValidator}
                  />
                </Grid>
              )}
            </Grid>
          </Grid>
          <Grid
            item
            xs={12}
            md={6}
            sx={{
              alignContent: 'flex-end',
            }}
          >
            <Button
              variant="contained"
              fullWidth
              sx={styles.createBtn}
              endIcon={<NextImage src={'/assets/icons/Ai.svg'} alt="" width={24} height={24} />}
              onClick={handleimageGenerate}
              disabled={isFetchingRequiredCredits || imageGenerationLoading}
            >
              Create
            </Button>
          </Grid>
        </Grid>
      </Grid>

      {openRenderGateModal && (
        <RenderGateModal
          open={openRenderGateModal}
          mode={renderGateModalMode}
          onClose={() => {
            setOpenRenderGateModal(false);
            setRenderGateModalMode('processing');
          }}
          userPlanData={userPlanData}
          agentPlanData={agentPlanData}
          push={push}
        />
      )}

      <CreditDialog
        isOpen={isCreditDialogOpen}
        onClose={closeCreditDialog}
        isFetchingCreditDetail={isFetchingRequiredCredits}
        onConfirm={handleConfirmGeneration}
        creditDetail={{
          availableCredit: requiredCreditsData?.availableCredits,
          creditNeeded: requiredCreditsData?.requiredCredits,
          creditsInUse: requiredCreditsData?.creditsInUse,
        }}
        isLoading={imageGenerationLoading}
        onUpgrade={handleUpgrade}
        enoughCreditConfirmBtnTitle={'Generate'}
        enoughCreditModalTitle="Confirm Generation"
        upgradeButtonTitle="Upgrade"
      />

      {isProccessingModalOpen && (
        <TextToImageProccessingModal
          handleCloseDialog={closeProccessingModal}
          shouldOpenDialog={isProccessingModalOpen}
          data={newGenerationData}
        />
      )}

      {isOpenBrowseBRollModal && (
        <BrowseBRollModal
          handleImageSelect={handleImageSelect}
          handleCloseDialog={CloseBrowseBRollModal}
          shouldOpenDialog={isOpenBrowseBRollModal}
          isImage={true}
          isBrollPage={true}
        />
      )}
    </Grid>
  );
};

const styles: Styles = {
  textField: {
    '& .MuiOutlinedInput-root': {
      border: '1px solid',
      borderRadius: '6px',
      borderColor: (theme) => theme.palette.background.ternary,
      backgroundColor: (theme) => theme.palette.background.secondary,
      '&:hover': {
        borderColor: (theme) => theme.palette.background.activeBorder,
      },
    },
    '& .MuiOutlinedInput-input::placeholder': {
      color: (theme) => theme.palette.text.placeholder,
    },
    '& input[type=number]::-webkit-inner-spin-button, & input[type=number]::-webkit-outer-spin-button':
      {
        webkitAppearance: 'auto',
        appearance: 'auto',
      },
    '& input[type=number]': {
      mozAppearance: 'textfield',
    },
  },
  heading: {
    fontSize: 14,
    fontWeight: 500,
    color: (theme) => theme.palette.text.primary,
    marginBottom: '5px',
    marginTop: '5px',
  },
  label: {
    fontSize: 14,
    fontWeight: 400,
    color: (theme) => theme.palette.text.primary,
    marginBottom: '5px',
  },
  createBtn: {
    py: 3,
    fontSize: 16,
    height: '56px',
    color: (theme) => theme.palette.text.primary,
    boxShadow: 'none',
  },
  dropdownProps: {
    maxHeight: '175px',
    maxWidth: 200,
    bgcolor: (theme) => theme.palette.background.secondary,
    '& .MuiMenuItem-root': {
      backgroundColor: (theme) => theme.palette.background.secondary,
      '&:hover': {
        backgroundColor: (theme) => theme.palette.background.activeCard,
      },
    },
  },
};
